const trimTrailingSlash = (value?: string) => (value || '').replace(/\/$/, '');

function assertServerRuntime() {
  if (typeof window !== 'undefined') {
    throw new Error('privateEnv can only be used on the server.');
  }
}

const isDev = process.env.NODE_ENV === 'development';
const isProd = process.env.NODE_ENV === 'production';

export const publicEnv = {
  baseUrl: trimTrailingSlash(
    process.env.NEXT_PUBLIC_BASE_URL || 'http://localhost:3000'
  ),
  apiDomain: trimTrailingSlash(
    process.env.NEXT_PUBLIC_API_DOMAIN || 'https://api.dev.upzi.vn'
  ),
  aiApiDomain: trimTrailingSlash(process.env.NEXT_PUBLIC_AI_API_DOMAIN),
  imageCdnUrl: trimTrailingSlash(process.env.NEXT_PUBLIC_IMAGE_CDN_URL),
  // imageUrl: trimTrailingSlash(process.env.NEXT_PUBLIC_IMAGE_URL),
  imageUrl: 'https://images.upzi.vn',
  bookHeroBgUrl:
    process.env.NEXT_PUBLIC_BOOK_HERO_BG_URL?.trim() ||
    '/images/book/hero-bg.svg',
  posthogKey: process.env.NEXT_PUBLIC_POSTHOG_KEY || '',
  posthogHost: trimTrailingSlash(process.env.NEXT_PUBLIC_POSTHOG_HOST),
  packageApp: process.env.NEXT_PUBLIC_PACKAGE_APP || '',
  iosDeepLink: process.env.NEXT_PUBLIC_IOS_DEEP_LINK || '',
  trackingApiUrl: trimTrailingSlash(
    process.env.NEXT_PUBLIC_TRACKING_API_URL ||
      process.env.NEXT_PUBLIC_TRACKING_URL
  ),
  googleClientId: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || '',
  services: {
    jobSeekersInfo: trimTrailingSlash(
      process.env.NEXT_PUBLIC_JOB_SEEKERS_INFO_SERVICE_URL
    ),
    jobSeeker: trimTrailingSlash(
      process.env.NEXT_PUBLIC_JOB_SEEKER_SERVICE_URL
    ),
    authentication: trimTrailingSlash(
      process.env.NEXT_PUBLIC_AUTHENTICATION_SERVICE_URL
    ),
    communityHub: trimTrailingSlash(
      process.env.NEXT_PUBLIC_COMMUNITY_HUB_SERVICE_URL
    ),
    notification: trimTrailingSlash(
      process.env.NEXT_PUBLIC_NOTIFICATION_SERVICE_URL
    ),
  },
} as const;

export type PublicEnv = typeof publicEnv;

export const getPublicEnv = () => publicEnv;

export function getPrivateEnv() {
  assertServerRuntime();

  return {
    googleAuthSecret: process.env.AUTH_GOOGLE_SECRET || '',
    shortLinksApiUrl: trimTrailingSlash(process.env.SHORT_LINKS_API_URL),
    trackingUrl: trimTrailingSlash(process.env.TRACKING_URL),
    services: {
      jobSeekersInfo: trimTrailingSlash(
        process.env.JOB_SEEKERS_INFO_SERVICE_URL
      ),
      jobSeeker: trimTrailingSlash(process.env.JOB_SEEKER_SERVICE_URL),
      authentication: trimTrailingSlash(process.env.AUTHENTICATION_SERVICE_URL),
      communityHub: trimTrailingSlash(process.env.COMMUNITY_HUB_SERVICE_URL),
    },
  } as const;
}

export type PrivateEnv = ReturnType<typeof getPrivateEnv>;

export const privateEnv = new Proxy({} as PrivateEnv, {
  get(_, property: keyof PrivateEnv) {
    return getPrivateEnv()[property];
  },
});

export const SERVICES = {
  JOB_SEEKERS_INFO: 'jobSeekersInfo',
  JOB_SEEKER: 'jobSeeker',
  AUTHENTICATION: 'authentication',
  COMMUNITY_HUB: 'communityHub',
} as const;

export type ServiceKey = (typeof SERVICES)[keyof typeof SERVICES];

export const getClientEnv = () => ({
  apiDomain: publicEnv.apiDomain,
  aiApiDomain: publicEnv.aiApiDomain,
  jobSeekersInfoServiceUrl: publicEnv.services.jobSeekersInfo,
  jobSeekerServiceUrl: publicEnv.services.jobSeeker,
  authenticationServiceUrl: publicEnv.services.authentication,
  communityHubServiceUrl: publicEnv.services.communityHub,
  notificationServiceUrl: publicEnv.services.notification,
});

export const getServerEnv = () => ({
  jobSeekersInfoServiceUrl: process.env.JOB_SEEKERS_INFO_SERVICE_URL || '',
  jobSeekerServiceUrl: process.env.JOB_SEEKER_SERVICE_URL || '',
  authenticationServiceUrl: process.env.AUTHENTICATION_SERVICE_URL || '',
  communityHubServiceUrl: process.env.COMMUNITY_HUB_SERVICE_URL || '',
});

export function getServiceUrl(
  service: ServiceKey,
  context: 'server' | 'client' = 'client'
) {
  const effectiveContext = isDev ? 'client' : context;
  const env = effectiveContext === 'server' ? getServerEnv() : getClientEnv();

  const serviceMap: Record<ServiceKey, string> = {
    [SERVICES.JOB_SEEKERS_INFO]: env.jobSeekersInfoServiceUrl,
    [SERVICES.JOB_SEEKER]: env.jobSeekerServiceUrl,
    [SERVICES.AUTHENTICATION]: env.authenticationServiceUrl,
    [SERVICES.COMMUNITY_HUB]: env.communityHubServiceUrl,
  };

  return serviceMap[service];
}

export function getAllServiceUrls(context: 'server' | 'client' = 'client') {
  const effectiveContext = isDev ? 'client' : context;
  const env = effectiveContext === 'server' ? getServerEnv() : getClientEnv();

  return {
    [SERVICES.JOB_SEEKERS_INFO]: env.jobSeekersInfoServiceUrl,
    [SERVICES.JOB_SEEKER]: env.jobSeekerServiceUrl,
    [SERVICES.AUTHENTICATION]: env.authenticationServiceUrl,
    [SERVICES.COMMUNITY_HUB]: env.communityHubServiceUrl,
  };
}

export const ENV_INFO = {
  nodeEnv: process.env.NODE_ENV,
  isDev,
  isProd,
  apiDomain: publicEnv.apiDomain,
};
